import classes from 'classnames';
import SelectWithDescriptions, {
  SelectDescType,
} from 'components/SelectWithDescriptions/SelectWithDescriptions';
import EducationEmploymentInput from 'pages/Registration/EducationEmployment/EducationEmploymentInput';
import { useFinance } from 'pages/Registration/Finance/useFinance';
import { Button, Input, Modal, Select, Stepper } from 'components';
import i18Next from 'i18next';
import { useTranslation } from 'react-i18next';
import H1 from 'components/H/H1';
import styles from './Finance.module.scss';

export default function Finance() {
  const { t } = useTranslation('finance');
  const {
    formik,
    ownershipOptions,
    showErrorField,
    isSubmitting,
    isLoadingInitialData,
    handleSubmitBtnOrModal,
    showModal,
    isFormikValidationsOKWithoutEmployer,
    professionOptions,
    professionSelected,
    setProfessionSelected,
    isFormikValidationsOK,
    setShowModal,
    educationOptions,
    incomeOptions,
    isOSVC,
  } = useFinance();

  return (
    <div className={styles.layout}>
      <H1 dataTest="finance-heading" className="text-center lg:text-left pl-2">
        {t('heading_modal')}
      </H1>
      <form
        onSubmit={formik.handleSubmit}
        className={styles.form}
        data-test="finance-form"
      >
        <div
          className={classes(
            styles.row,
            showErrorField('incomeSourceType') && styles['row-error'],
          )}
          data-test="finance-incomeSource-row"
        >
          <Select
            className={styles['field-select']}
            name="incomeSourceType"
            onChange={formik.handleChange}
            options={incomeOptions}
            value={formik.values.incomeSourceType}
            error={formik.errors.incomeSourceType}
            errorClassName={styles['error-msg']}
            isTouched={formik.touched.incomeSourceType}
            disabled={isSubmitting}
            dataTest="finance-incomeSource-value"
            placeholder={t('income_source_type')}
          />
          <div className={styles['field-desc']}>
            {t('income_source_type_desc')}
          </div>
        </div>
        <div
          className={classes(
            styles.row,
            showErrorField('monthlyIncome') && styles['row-error'],
          )}
          data-test="finance-monthlyIncome-row"
        >
          <Input
            name="monthlyIncome"
            dataTest="finance-monthlyIncome-value"
            value={formik.values.monthlyIncome}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.monthlyIncome}
            errorClassName={styles['error-msg']}
            postFix={i18Next.t('common:currency')}
            disabled={isSubmitting}
            isTouched={formik.touched.monthlyIncome}
            type="number"
            placeholder={t('monthly_income_placeholder')}
          />
          <div className={styles['field-desc']}>{t('monthly_income_desc')}</div>
        </div>
        <div
          className={classes(
            styles.row,
            showErrorField('monthlyExpenses') && styles['row-error'],
          )}
          data-test="finance-monthlyExpenses-row"
        >
          <Input
            name="monthlyExpenses"
            dataTest="finance-monthlyExpenses-value"
            value={formik.values.monthlyExpenses}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            isTouched={formik.touched.monthlyExpenses}
            error={formik.errors.monthlyExpenses}
            errorClassName={styles['error-msg']}
            postFix={i18Next.t('common:currency')}
            disabled={isSubmitting}
            type="number"
            placeholder={t('monthly_expenses')}
          />
          <div className={styles['field-desc']}>
            {t('monthly_expenses_desc')}
          </div>
        </div>
        <div
          className={classes(
            styles.row,
            showErrorField('educationType') && styles['row-error'],
          )}
          data-test="finance-educationType-row"
        >
          <Select
            className={styles['field-select']}
            name="educationType"
            onChange={formik.handleChange}
            options={educationOptions}
            value={formik.values.educationType}
            error={formik.errors.educationType}
            errorClassName={styles['error-msg']}
            isTouched={formik.touched.educationType}
            disabled={isSubmitting}
            dataTest="finance-educationType-value"
            placeholder={t('education_type')}
          />
        </div>
        <div
          className={classes(
            styles.row,
            showErrorField('housingType') && styles['row-error'],
          )}
          data-test="finance-housingType-row"
        >
          <Select
            className={styles['field-select']}
            name="housingType"
            onChange={formik.handleChange}
            options={ownershipOptions}
            value={formik.values.housingType}
            disabled={!ownershipOptions?.length || isSubmitting}
            error={formik.errors.housingType}
            errorClassName={styles['error-msg']}
            dataTest="finance-housingType-value"
            isTouched={formik.touched.housingType}
            placeholder={t('housing_type')}
          />
        </div>
        <div
          className={classes(
            styles.row,
            showErrorField('numberOfHouseholdMembers') && styles['row-error'],
          )}
          data-test="finance-householdMembers-row"
        >
          <div
            className={styles['row-title']}
            data-test="finance-householdMembers-label"
          >
            {t('household_members')}
          </div>
          <Stepper
            dataTest="household-members"
            min={1}
            max={20}
            name="numberOfHouseholdMembers"
            value={Number(formik.values.numberOfHouseholdMembers || '0')}
            onChange={(name, newValue) => {
              formik.setFieldValue(name, newValue);
              if (
                Number(formik.values.numberOfEmployedHouseholdMembers || '0') >
                newValue
              ) {
                formik.setFieldValue(
                  'numberOfEmployedHouseholdMembers',
                  newValue,
                );
              }
            }}
            disabled={isSubmitting}
          />
        </div>
        <div
          className={classes(
            styles.row,
            showErrorField('numberOfEmployedHouseholdMembers') &&
              styles['row-error'],
          )}
          data-test="finance-numberOfEmployedHouseholdMembers-row"
        >
          <div
            className={styles['row-title']}
            data-test="finance-numberOfEmployedHouseholdMembers-label"
          >
            {t('employed_household_members')}
          </div>
          <Stepper
            dataTest="finance-numberOfEmployedHouseholdMembers-value"
            min={1}
            max={formik.values.numberOfHouseholdMembers}
            name="numberOfEmployedHouseholdMembers"
            value={Number(
              formik.values.numberOfEmployedHouseholdMembers || '0',
            )}
            onChange={formik.setFieldValue}
            error={formik.errors.numberOfEmployedHouseholdMembers}
            disabled={isSubmitting}
            isTouched
          />
        </div>
        {showModal && (
          <Modal
            onClose={() => setShowModal(false)}
            className={styles.modal}
            containerClassName={styles['modal-container']}
            dataTest="modalFinance"
          >
            <div className={styles.title} data-test="finance-modal-title">
              {t('modal.title')}
            </div>
            <div
              dangerouslySetInnerHTML={{
                __html: isOSVC ? t('modal.desc_osvc') : t('modal.desc'),
              }}
              data-test="finance-modal-desc"
            />
            <div
              className={classes(
                styles.row,
                showErrorField('employer') && styles['row-error'],
              )}
              data-test="finance-employer-row"
            >
              <div
                className={styles['row-title']}
                data-test="finance-employer-label"
              >
                {isOSVC ? t('employer_osvc') : t('employer')}
              </div>
              <EducationEmploymentInput
                error={formik.errors.employer}
                errorClassName={styles['error-msg']}
                onChange={(newValue) => {
                  formik.setFieldValue('employer', newValue);
                }}
                value={formik.values.employer}
                disabled={isSubmitting}
                isTouched={formik.touched.employer}
                isOSVC={isOSVC}
              />
            </div>
            {professionOptions && (
              <div className={styles.row} data-test="finance-profession-row">
                <div
                  className={styles['row-title']}
                  data-test="finance-profession-label"
                >
                  {t('profession')}
                </div>
                <SelectWithDescriptions
                  className={styles['field-select']}
                  options={professionOptions}
                  optionSelected={professionSelected}
                  onOptionSelect={(newProfession: SelectDescType) =>
                    setProfessionSelected(newProfession)
                  }
                  disabled={isSubmitting}
                  dataTest="finance-profession"
                  placeholder={t('profession_placeholder')}
                />
              </div>
            )}
            <Button
              dataTest="finance-submit"
              onClick={handleSubmitBtnOrModal}
              disabled={
                isSubmitting || !isFormikValidationsOK || !professionSelected
              }
              pending={isSubmitting}
            >
              {t('btn')}
            </Button>
          </Modal>
        )}
        <div className={styles.center}>
          <Button
            className={styles['submit-btn']}
            dataTest="finance-submit"
            onClick={handleSubmitBtnOrModal}
            disabled={
              isSubmitting ||
              isLoadingInitialData ||
              !isFormikValidationsOKWithoutEmployer
            }
            pending={isLoadingInitialData || isSubmitting}
          >
            {t('btn')}
          </Button>
        </div>
      </form>
    </div>
  );
}
